import { MomentObj } from "@veneer/core";
import moment from "moment";
import { useState } from "react";
import justificationAbsencesService from "../../services/userManagementApi/justificationAbsences.service";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { useTranslation } from "react-i18next";
import { CellFormulaValue, Workbook } from "exceljs";
import { cellsFileTemplateExcelEnum } from "../../models/enum/justificationAbsences/justificationAbsencesEnum";
import { ICreateJustificationAbsenceDto, ICustomPropertiesFile, IPropsComponentesTab, IResponseCreateJustificationAbsencesDto, IUploadJustificacationAbsencesDto } from "../../models/dto/justificationAbsences/IJustificationAbsenceModelDto";

export const useCreateJustifications = (props: IPropsComponentesTab) => {
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [t] = useTranslation("global");

  const [openModalHelp, setOpenModalHelp] = useState(false);
  const [ startDate, setStartDate ] = useState<MomentObj | undefined>();
  const [ endDate, setEndDate ] = useState<MomentObj | undefined>();
  const [buttonSaveAbsencesFileDisabled, setButtonSaveAbsencesFileDisabled] = useState(true);
  const [jsonAbsences, setJsonAbsences] = useState<ICreateJustificationAbsenceDto[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [openModalUploadFile, setOpenModalUploadFile] = useState(false);
  const [propertiesFile, setPropertiesFile] = useState<ICustomPropertiesFile>();
  const [helperFilePicker, setHelperFilePicker] = useState<string>("");

  const labelsResponseCreateAbsencesInBulk = [
    t('modal.unexpectedError'), // 0
    t('modal.justificationAbsenceCreated'), // 1
    t('modal.justificationAbsenceNotFound'), // 2
    t('modal.justificationAbsenceTypeNotFound'), // 3
    t('modal.justificationAbsenceDuplicate'), // 4
    t('modal.editJustificationDisabled'), // 5
  ]

  const handleOnDateRangeChange = (newDate: any) => {
    const { startDate: newStartDate, endDate: newEndDate } = newDate;

    if ((newStartDate && dateOutsideRange(newStartDate)) || (newEndDate && dateOutsideRange(newEndDate)))
      return false;

    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const dateOutsideRange = (date: MomentObj) => {
    
    const startDateLimitBefore = moment().clone().add(-2, "days");
    const validationStartDateLimitBefore =
      date.isBefore(startDateLimitBefore, "day");
    return validationStartDateLimitBefore || date.isAfter(moment(), "day");
  }

  const onClickConsultInformation = async () => {

    if (!startDate || !endDate)
      return;
    
    props.setLoading(true);
    const newStartDate = startDate.format("MM/DD/YYYY");
    const newEndDate = endDate.format("MM/DD/YYYY");
    
    try {
      await justificationAbsencesService.GetAbsencesWithoutJustificationFile(Number(languageID.value), newStartDate, newEndDate)
      .then((r: Blob | any) => {
        try {
          if (r?.statusCode === 401) {
            console.error('Error:', "Download failed");
          } else if (r instanceof Blob) {
            try {
  
              let fileName = 'absences_without_justification';
  
              const url = window.URL.createObjectURL(r);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', fileName + '.xlsx');
              document.body.appendChild(link);
              link.click();
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.error('Error:', error);
            }
          }
        } catch (error) {
          console.error('Error:', error);
        }
      })
    } catch (error) {
      
      console.error(error);
      props.setOpenModalError(true);
      props.setMessageModalError(t("modal.unexpectedError"));
    } finally {
      
      props.setLoading(false)
    }
  }

  const handleOnClickUploadFile = () => {
    setOpenModalUploadFile(true);
    setFileName("");
    setErrorFilePicker(false);
    setJsonAbsences([]);
    props.setMessageModalError("");
    setButtonSaveAbsencesFileDisabled(true);
    setPropertiesFile(undefined);
    setHelperFilePicker("");
  };

  const handleOnClickExitFileUpload = () => {
    setFileName("");
    setOpenModalUploadFile(false);
    setButtonSaveAbsencesFileDisabled(true);
    props.setMessageModalError("");
    setPropertiesFile(undefined);
    setJsonAbsences([]);
    setHelperFilePicker("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = e.target;
    if (!fileInput.files) {
      props.setMessageModalError(t("modal.errorLoadingFile"));
      props.setOpenModalError(true);
      setErrorFilePicker(true);
      return;
    }
    
    const file = fileInput.files[0];
    setFileName(file.name);
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();
    
    if (fileType?.toLowerCase() !== "xlsx") {
      props.setMessageModalError(t("modal.errorLoadingFile"));
      props.setOpenModalError(true);
      setErrorFilePicker(true);
      return;
    }
    
    props.setLoadingModal(true);
    setErrorFilePicker(false);
    reader.onload = async (event) => {
      const data = event.target?.result as ArrayBuffer;
      const workbook = new Workbook();

      try {
        await workbook.xlsx.load(data);
        const hiddenSheet = workbook.getWorksheet("Data");
        const _metadata = workbook.getWorksheet("Metadata");
        var absencesTypeMap: Record<string, number> = {};
        var advisorsMap: Record<string, number> = {};
        let customProperties: ICustomPropertiesFile;
        const rows: ICreateJustificationAbsenceDto[] = [];

        if (_metadata) {

          customProperties = {
            userIDConsultedData: Number(_metadata.getCell(`B1`).value),
            startDate: _metadata.getCell(`B2`).value?.toString().trim() ?? "",
            endDate: _metadata.getCell(`B3`).value?.toString().trim() ?? "",
          };
        } else {
          props.setMessageModalError(t("modal.errorLoadingFile"));
          props.setOpenModalError(true);
          setErrorFilePicker(true);
          props.setLoadingModal(false);
          return;
        }
        

        if (hiddenSheet) {
          
          // set map of data list
          // absences type list
          let row = 1;
          while (hiddenSheet.getCell(`A${row}`).value && hiddenSheet.getCell(`B${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`A${row}`).value);
            const propertyName = hiddenSheet.getCell(`B${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !absencesTypeMap[propertyName?.trim()]) {
              absencesTypeMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // advisors list
          row = 1;
          while (hiddenSheet.getCell(`C${row}`).value && hiddenSheet.getCell(`D${row}`).value) {
            const propertyID = Number(hiddenSheet.getCell(`C${row}`).value);
            const propertyName = hiddenSheet.getCell(`D${row}`).value?.toString().trim();
            
            if (propertyName?.trim() && !advisorsMap[propertyName?.trim()]) {
              advisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        } else {
          props.setMessageModalError(t("modal.errorLoadingFile"));
          props.setOpenModalError(true);
          setErrorFilePicker(true);
          props.setLoadingModal(false);
          return;
        }

        const sheet = workbook.worksheets[0];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            return;
          }
         
          // if not register absence for the appointment be ommited
          if (!getValueCellTemplate(row.getCell(cellsFileTemplateExcelEnum.absenceTypeName).value)) {
            return;
          }

          let advisorName = getValueCellTemplate(row.getCell(cellsFileTemplateExcelEnum.advisorsName).value);
          let posCode = getValueCellTemplate(row.getCell(cellsFileTemplateExcelEnum.posCode).value);
          let sheduled = getValueCellTemplate(row.getCell(cellsFileTemplateExcelEnum.sheduledAppointment).value);
          let absenceTypeName = getValueCellTemplate(row.getCell(cellsFileTemplateExcelEnum.absenceTypeName).value);

          if (absenceTypeName && absencesTypeMap[absenceTypeName] &&
              advisorName && advisorsMap[advisorName]
          ) {
            const rowData: ICreateJustificationAbsenceDto = {
              UserID: advisorsMap[advisorName],
              PosCode: posCode ?? "",
              VisitScheduled: moment(sheduled, 'DD/MM/YYYY').format('MM/DD/YYYY'),
              AbsenceTypeID: absencesTypeMap[absenceTypeName]
            };

            rows.push(rowData);
          } else {
            setErrorFilePicker(true);
            props.setMessageModalError(t("modal.invalidDataUsersMassive").replace('{0}', rowNumber.toString()));
            props.setOpenModalError(true);
            return;
          }
        });

        if (rows.length === 0) {

          setErrorFilePicker(true);
          props.setMessageModalError(t("modal.dataNotFoundInFile"));
          props.setOpenModalError(true);
          props.setLoadingModal(false);
          return;
        }

        setHelperFilePicker(t("modal.recordsFoundToProccess").replace("{0}", rows.length.toString()));
        setButtonSaveAbsencesFileDisabled(false);
        setPropertiesFile(customProperties);
        setJsonAbsences(rows);
      } catch (error) {

        console.error(error);
        setErrorFilePicker(true);
        props.setMessageModalError(t("modal.errorLoadingFile"));
        props.setOpenModalError(true);
      } finally {

        props.setLoadingModal(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const onClearFilePicker = () => {
    setFileName("");
    setJsonAbsences([]);
    setErrorFilePicker(false);
    props.setMessageModalError("");
    setButtonSaveAbsencesFileDisabled(true);
    setPropertiesFile(undefined);
    setHelperFilePicker("");
  };

  const submitJustificationAbsencesFile = async () => {

    props.setLoadingModal(true);
    const dataRequest: IUploadJustificacationAbsencesDto = {
      StartDate: propertiesFile?.startDate ?? "",
      EndDate: propertiesFile?.endDate ?? "",
      JustificationAbsences: jsonAbsences,
      UserIDConsultedData: propertiesFile?.userIDConsultedData ?? 0
    }
    try {
      const response: IResponseCreateJustificationAbsencesDto[] = await justificationAbsencesService.CreateJustificationAbsencesInBulk(dataRequest);
 
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Datos");

      worksheet.columns = [
        { header: t("header.userId"), key: "userID", width: 15 },
        { header: t("header.name"), key: "advisor", width: 30 },
        { header: t("header.pointOfSaleCode"), key: "posCode", width: 15 },
        { header: t("header.visitDate"), key: "appointmentDate", width: 15 },
        { header: t("modal.message"), key: "message", width: 30 },
      ];

      response.forEach((item: IResponseCreateJustificationAbsencesDto) => {

        item.message = labelsResponseCreateAbsencesInBulk[item.result] ?? labelsResponseCreateAbsencesInBulk[0];
        
        const row = worksheet.addRow(item);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "00B0F0" } };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
        cell.font = { bold: true, color: { argb: "ffffff"} };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "justification_absences_result.xlsx";
      a.click();
      URL.revokeObjectURL(url);
      
      props.setOpenModalMessage(true);
      props.setMessageModalMessage(t('modal.messageAbsences'));

      props.callBackOnChangeTab(1);
    } catch (error) {

      console.error(error);
      props.setOpenModalError(true);
      props.setMessageModalError(t("modal.unexpectedError"));
    } finally {

      setOpenModalUploadFile(false);
      props.setLoadingModal(false);
      setPropertiesFile(undefined);
      setJsonAbsences([]);
    }
  };

  const getValueCellTemplate = (cellValue: any): string | undefined => {
  
    if (typeof cellValue === 'string' || typeof cellValue === 'number') {
      return cellValue?.toString().trim();
    }

    if ((cellValue as CellFormulaValue)?.result) {
      return (cellValue as CellFormulaValue)?.result?.toString().trim();
    }

    return;
  }
  
  return {
    handleOnDateRangeChange,
    dateOutsideRange,
    onClickConsultInformation,
    startDate,
    endDate,
    handleOnClickUploadFile,
    openModalUploadFile,
    handleOnClickExitFileUpload,
    buttonSaveAbsencesFileDisabled,
    submitJustificationAbsencesFile,
    errorFilePicker,
    fileName,
    handleFileChange,
    onClearFilePicker,
    helperFilePicker,
    setOpenModalHelp,
    openModalHelp,
  };
}